import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Mountain, DollarSign, FolderKanban, TrendingUp, Recycle } from 'lucide-react';
import { revealVariants, staggerContainer, cardVariants } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

const sectors = [
  {
    icon: Cpu,
    title: "Technology Consulting",
    description: "Digital transformation, cybersecurity, and data strategy solutions.",
    gradient: "from-blue-500 to-purple-600"
  },
  {
    icon: Mountain,
    title: "Mining & Survey",
    description: "Geological assessments, site compliance, and equipment optimization.",
    gradient: "from-amber-500 to-orange-600"
  },
  {
    icon: DollarSign,
    title: "Financial Consulting",
    description: "Corporate planning, investment strategy, and risk management.",
    gradient: "from-green-500 to-emerald-600"
  },
  {
    icon: FolderKanban,
    title: "Project Management",
    description: "End-to-end project delivery with precision and excellence.",
    gradient: "from-indigo-500 to-blue-600"
  },
  {
    icon: TrendingUp,
    title: "Business Advisory",
    description: "Strategic planning and operational excellence consulting.",
    gradient: "from-purple-500 to-pink-600"
  },
  {
    icon: Recycle,
    title: "Sustainability & Growth",
    description: "Sustainable business practices and long-term growth strategies.",
    gradient: "from-teal-500 to-cyan-600"
  }
];

export const MultiSectorExcellence = () => {
  const [ref, isInView] = useInViewOnce();

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 3 + Math.random() * 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={revealVariants}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-6"
          >
            <motion.div 
              className="w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span>Our Expertise</span>
          </motion.div>
          
          <motion.h2 
            variants={revealVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Multi-Sector <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Excellence</span>
          </motion.h2>
          
          <motion.p 
            variants={revealVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Delivering specialized expertise across multiple industries with innovative solutions 
            that drive sustainable growth and operational excellence.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {sectors.map((sector, index) => {
            const IconComponent = sector.icon;
            return (
              <motion.div
                key={sector.title}
                variants={revealVariants}
                className="group"
                custom={index}
              >
                <motion.div
                  variants={cardVariants}
                  whileHover="hover"
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 relative overflow-hidden h-full cursor-pointer"
                >
                  {/* Gradient Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-[#FF5C8A]/5 to-[#FF8C42]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Background Pattern */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#FF5C8A]/10 to-[#FF8C42]/10 rounded-full transform translate-x-16 -translate-y-16 group-hover:scale-125 transition-transform duration-500" />
                  
                  <div className="relative z-10">
                    {/* Animated Icon */}
                    <motion.div 
                      className="w-16 h-16 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                      whileHover={{ 
                        background: "linear-gradient(45deg, #FF5C8A, #FF8C42)",
                        rotate: [0, 10, -10, 0]
                      }}
                      transition={{ duration: 0.6 }}
                    >
                      <IconComponent size={32} className="text-white" />
                    </motion.div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1F3C88] transition-colors duration-300">
                      {sector.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {sector.description}
                    </p>

                    {/* Animated Accent Line */}
                    <motion.div
                      className="w-0 h-1 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full mt-4 group-hover:w-full transition-all duration-500"
                    />
                  </div>

                  {/* Hover Shimmer Effect */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
                  </motion.div>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          variants={revealVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mt-16"
        >
          <motion.div
            className="inline-block bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg"
            whileHover={{ scale: 1.02 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to explore our multi-sector capabilities?
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Discover how our cross-industry expertise can bring innovative solutions to your challenges.
            </p>
            <motion.a
              href="/services"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-shadow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Explore All Services</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                →
              </motion.div>
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};